import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';

interface OAuthLoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    fullName: string | null;
    avatarUrl: string | null;
    isSuperAdmin: boolean;
  };
  isNewUser: boolean;
  expiresAt: string;
}

type AuthState = 'loading' | 'success' | 'error';

export function GoogleCallbackPage() {
  const navigate = useNavigate();
  const { setTokenAndFetchUser } = useAuthStore();
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function handleCallback() {
      try {
        // Get tokens from URL fragment (hash)
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        
        const accessToken = params.get('access_token');
        const idToken = params.get('id_token');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        if (error) {
          throw new Error(errorDescription || error);
        }

        if (!accessToken && !idToken) {
          throw new Error('No tokens received from Google');
        }

        // Verify with backend
        const response = await api.post<OAuthLoginResponse>('/auth-providers/google/verify', {
          accessToken: accessToken || undefined,
          idToken: idToken || undefined,
        });

        await setTokenAndFetchUser(response.token);
        setAuthState('success');

        // If opened as popup, communicate with opener and close
        if (window.opener) {
          window.opener.postMessage({
            type: 'google-auth-success',
            token: response.token,
            user: response.user,
          }, window.location.origin);
          
          setTimeout(() => {
            window.close();
          }, 1500);
        } else {
          // Redirect to app
          setTimeout(() => {
            navigate('/app');
          }, 1500);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Google authentication failed';
        setError(message);
        setAuthState('error');

        // If opened as popup, communicate error
        if (window.opener) {
          window.opener.postMessage({
            type: 'google-auth-error',
            error: message,
          }, window.location.origin);
        }
      }
    }

    handleCallback();
  }, [navigate, setTokenAndFetchUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-main p-4">
      <div className="w-full max-w-md text-center">
        <div className="bg-neutral-0 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 shadow-sm p-8">
          {authState === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-accent-500 animate-spin" />
              <h2 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mt-4">
                Signing you in...
              </h2>
              <p className="text-sm text-neutral-500 mt-2">
                Please wait while we verify your Google account
              </p>
            </>
          )}

          {authState === 'success' && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto text-success-500" />
              <h2 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mt-4">
                Success!
              </h2>
              <p className="text-sm text-neutral-500 mt-2">
                {window.opener 
                  ? 'You can close this window now'
                  : 'Redirecting to dashboard...'}
              </p>
            </>
          )}

          {authState === 'error' && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-error-500" />
              <h2 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mt-4">
                Authentication Failed
              </h2>
              <p className="text-sm text-error-600 mt-2">
                {error}
              </p>
              <div className="mt-6 space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="btn btn-primary w-full"
                >
                  Back to Login
                </button>
                {window.opener && (
                  <button
                    onClick={() => window.close()}
                    className="btn btn-secondary w-full"
                  >
                    Close Window
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
